import { DataTypes as DT } from 'business/definitions';
import {
    BUDGET_LINE_ID,
    BUDGET_LINE_STATUS_OBSOLETE,
} from 'routes/BudgetModule/config/constants';
import {
    BudgetProposalT,
    FieldSettingsT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import { getAggStatus } from 'routes/BudgetModule/helpers/statusHelpers';
import { ExpenseIncomeImputationDataT } from '../../hooks/expenseIncomeImputations';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { Phase } from 'business/constants';

const getVisibleMoneyImputationColumns = (
    columnsSettings: FieldSettingsT[],
    columnDetails: ResponseColumnT[],
): string[] => {
    // empty columnSettings means that every column is visible
    const visibleColumns =
        columnsSettings.length > 0
            ? columnsSettings
                  .filter((column) => !column.hidden)
                  .map((column) => column.field)
            : columnDetails.map((column) => column.key);
    const moneyColumns = columnDetails
        .filter((column) => column.type === DT.money)
        .map((column) => column.key);
    return visibleColumns.filter((column) => moneyColumns.includes(column));
};

/** Returns True if all visible amounts of the imputation are 0 or null. */
const areImputationAmountsZero = (
    imputationData: ResponseDataT,
    columnsSettings: FieldSettingsT[],
    columnDetails: ResponseColumnT[],
): boolean => {
    const moneyVisibleColumns = getVisibleMoneyImputationColumns(
        columnsSettings,
        columnDetails,
    );
    return moneyVisibleColumns.every((columnField) =>
        columnField in imputationData
            ? imputationData[columnField] === 0
            : true,
    );
};

/** Based on ResponseDataValueT type, returns the value if type is a number or 0 */
const getNumberOrZero = (value: ResponseDataValueT): number =>
    typeof value === 'number' ? value : 0;

export const applyImputationsFilters = (
    imputationsData: ExpenseIncomeImputationDataT[],
    inscriptions: InscriptionT[],
    budgetProposal: BudgetProposalT,
    columnsSettings: FieldSettingsT[],
    columnDetails: ResponseColumnT[],
    hideEmptyBudgetLines: boolean,
    filterInscriptions: string,
    statusesSettings: StatusesSettingsT,
    phasesSettings: Phase[],
): ExpenseIncomeImputationDataT[] => {
    const hasImputationInscriptions = (
        imputation: ExpenseIncomeImputationDataT,
    ) =>
        inscriptions.filter(
            (inscription) =>
                inscription[BUDGET_LINE_ID] ===
                imputation[`${BUDGET_LINE_ID}..${budgetProposal.exercice}`],
        ).length > 0;

    if (filterInscriptions && filterInscriptions !== '') {
        return (
            imputationsData
                .filter(hasImputationInscriptions)
                // FIXME here we add a custom element to make AG-Grid detect the data change
                // and refresh the detailed grid when filter is updated
                .map((i) => ({
                    ...i,
                    filterLength: filterInscriptions.length,
                }))
        );
    }
    if (hideEmptyBudgetLines) {
        return imputationsData.filter((imputation) => {
            const hasBudgetLineForCurrentExercice =
                getNumberOrZero(
                    imputation[`${BUDGET_LINE_ID}..${budgetProposal.exercice}`],
                ) > 0;
            const imputationInscriptions = inscriptions.filter(
                (inscription) =>
                    inscription[BUDGET_LINE_ID] ===
                    imputation[`${BUDGET_LINE_ID}..${budgetProposal.exercice}`],
            );
            const isAMPBudgetableImputation =
                imputation.isAmpmetropoleBudgetable;

            const status = getAggStatus(
                imputationInscriptions,
                budgetProposal.phase,
                statusesSettings,
                phasesSettings,
                hasBudgetLineForCurrentExercice,
                isAMPBudgetableImputation,
            );
            const isHidden =
                status === BUDGET_LINE_STATUS_OBSOLETE &&
                areImputationAmountsZero(
                    imputation,
                    columnsSettings,
                    columnDetails,
                );
            return !isHidden;
        });
    }
    return imputationsData;
};
